// Document generation tool definition for Claude API
// Uses DocumentJSON format as input

import type { Tool } from '@anthropic-ai/sdk/resources/messages';

// ============================================================================
// Tool Definition
// ============================================================================

/**
 * Document generation tool
 * Creates Word documents (.docx) from JSON format
 *
 * The full JSON schema and examples are loaded via get_skill_instruction tool.
 * This simple schema accepts JSON content as a string.
 */
export const DOCUMENT_GENERATION_TOOL: Tool = {
  name: 'generate_document',
  description: `Generate a Word document (.docx) using JSON format.

## Quick Start

Use the get_skill_instruction tool to load full documentation:
\`\`\`json
{ "skill_name": "docx" }
\`\`\`

## Basic JSON Structure

\`\`\`json
{
  "type": "document",
  "title": "My Document",
  "sections": [{
    "children": [
      { "type": "heading", "level": 1, "text": "Title" },
      { "type": "paragraph", "text": "Regular text." },
      { "type": "paragraph", "runs": [
        { "text": "Normal " },
        { "text": "bold", "bold": true },
        { "text": " and " },
        { "text": "italic", "italic": true }
      ]},
      { "type": "bullets", "items": ["Point 1", "Point 2"] },
      { "type": "table", "headers": ["Name", "Value"], "rows": [["A", "1"], ["B", "2"]] }
    ]
  }]
}
\`\`\`

## Key Features

- Text runs for mixed formatting: bold, italic, underline, color, fontSize
- Tables with headers, rows, column widths, cell formatting
- Bullet and numbered lists with nesting (level: 0-8)
- Images via data URLs
- Page breaks, headers/footers with page numbers
- Multiple sections with independent page setup

When editing an existing document, include documentId:
{ "type": "document", "documentId": "existing-id", "title": "Updated Title", ... }`,

  input_schema: {
    type: 'object' as const,
    properties: {
      content: {
        type: 'string',
        description: 'JSON string containing a DocumentJSON object with type "document", title, and sections array.',
      },
    },
    required: ['content'],
  },
};

/**
 * Repair tool for batch-fixing syntax errors in cached malformed document JSON.
 * Applies multiple search/replace pairs in one call.
 */
export const REPAIR_DOCUMENT_JSON_TOOL: Tool = {
  name: 'repair_document_json',
  description: 'Fix syntax errors in cached malformed document JSON. Requires repairId from the error response. Applies multiple search/replace pairs in one call.',
  input_schema: {
    type: 'object' as const,
    properties: {
      repairId: {
        type: 'string',
        description: 'ID from a previous JSON parse error.',
      },
      repairs: {
        type: 'array',
        description: 'Search/replace pairs applied sequentially to cached JSON.',
        items: {
          type: 'object',
          properties: {
            searchText: {
              type: 'string',
              description: 'Exact substring to find.',
            },
            replaceText: {
              type: 'string',
              description: 'Replacement string.',
            },
          },
          required: ['searchText', 'replaceText'],
        },
      },
    },
    required: ['repairId', 'repairs'],
  },
};
